import { useIsAuthenticated } from '@azure/msal-react';
import { Link } from 'react-router-dom';
import { BarChart3, Calendar, FileText, Receipt, Users, type LucideIcon } from 'lucide-react';
import { useMe } from '../api/hooks';
import { usePageTitle } from '../hooks/usePageTitle';

const TILES: { title: string; description: string; href: string; icon: LucideIcon }[] = [
  { title: 'Receipts', description: 'Send receipts for deposits and payments', href: '/receipts', icon: Receipt },
  { title: 'Clients', description: 'Import contacts, maintain client info', href: '/customers', icon: Users },
  { title: 'Visits', description: 'Schedule, import/export, email invites', href: '/calendar', icon: Calendar },
  { title: 'Documents', description: 'Upload templates, send signable documents', href: '/documents', icon: FileText },
  { title: 'Reports', description: 'Client revenue and history, expiring documents', href: '/reports', icon: BarChart3 }
];

export function DashboardPage() {
  usePageTitle('Home');
  const isAuthenticated = useIsAuthenticated();
  const { data: me, isLoading } = useMe(isAuthenticated);

  if (!isAuthenticated) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-semibold text-ink">Welcome to ReceiptRaptor</h1>
        <p className="mt-2 text-slate-500">Sign in to issue receipts, archive agreements, and track visits.</p>
      </div>
    );
  }
  if (isLoading) return <div className="mx-auto max-w-3xl px-4 py-8 text-slate-500">Loading…</div>;

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-6 pb-24 sm:pb-8">
      <section>
        <h1 className="font-display text-2xl font-semibold text-ink">{me?.businessName ?? 'Your dashboard'}</h1>
        <p className="mt-1 text-sm text-slate-500">
          Signed in as {me?.email} ({me?.role === 'SitterAdmin' ? 'Site Admin' : me?.role})
        </p>
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        {TILES.map(({ title, description, href, icon: Icon }) => (
          <Link
            key={href}
            to={href}
            className="group flex items-start gap-3 rounded-2xl bg-surface p-5 shadow-sm ring-1 ring-slate-200/70 transition-colors hover:ring-pine"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-50 text-pine">
              <Icon size={20} />
            </span>
            <span className="min-w-0">
              <span className="block font-display font-semibold text-ink">{title}</span>
              <span className="mt-0.5 block text-sm text-slate-500">{description}</span>
            </span>
          </Link>
        ))}
      </section>
    </div>
  );
}
